/**
 * Nudge to reload once a newer build is deployed: an old tab keeps running old code against a
 * newer backend, which is how saves go wrong. A card in the bottom right that stays until the
 * page is refreshed, but never covers or blocks the page's own work.
 */

import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { hardReload } from '@/lib/hard-reload';

declare const __APP_VERSION__: string;

const CHECK_EVERY_MS = 5 * 60 * 1000;

interface Deployed {
  version: string;
  /** The semantic version, e.g. "1.4.0". */
  release?: string;
}

async function deployedVersion(): Promise<Deployed | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<Deployed>;
    return typeof body.version === 'string' ? (body as Deployed) : null;
  } catch {
    return null; // Offline or mid-deploy: try again on the next tick.
  }
}

export function UpdateNudge() {
  const [outdated, setOutdated] = useState(false);
  const [release, setRelease] = useState<string>();

  useEffect(() => {
    let found = false;
    const check = async () => {
      if (found || document.visibilityState !== 'visible') return;
      const deployed = await deployedVersion();
      if (deployed && deployed.version !== __APP_VERSION__) {
        found = true;
        setRelease(deployed.release);
        setOutdated(true);
      }
    };
    void check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);


  if (!outdated) return null;

  return (
    <div
      role='status'
      className='fixed right-4 bottom-4 z-50 flex w-80 gap-3 rounded-xl border border-gray-500 bg-white p-4 shadow-lg animate-in fade-in slide-in-from-bottom-2'
    >
      <RefreshCw className='mt-0.5 size-4 shrink-0 text-indigo-600' />
      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <p className='text-sm font-semibold text-ink'>{release ? `Kim AI v${release} is out` : 'A new version of Kim AI is out'}</p>
        <p className='text-xs text-gray-800'>Refresh when you’re ready — an older version can cause problems when saving.</p>
        <Button size='sm' className='mt-2 h-7 self-start text-xs' onClick={() => void hardReload()}>
          Refresh now
        </Button>
      </div>
    </div>
  );
}
